import { EditableContext } from './editableRow';
import { Form, Input, Select } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';

const { Option } = Select;

export default ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  dataSource,
  enableEdits,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    // Prompt the user to set the column name (required) when a new row is inserted
    // This also ensures validation is triggered for newly inserted rows
    if (record && dataIndex === 'column_name' && !record.column_name) {
      setEditing(true);
    } else {
      setEditing(false);
    }
  }, [record]);

  const toggleEdit = () => {
    setEditing(!editing);

    if (record.id) {
      form.setFieldsValue({ [`${dataIndex}-${record.id}`]: record[dataIndex] });
    } else {
      form.setFieldsValue({ [{ dataIndex }]: record[dataIndex] });
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      if (dataIndex !== 'nullable' && dataIndex !== 'required') {
        toggleEdit();
      }

      // The form items need to have unique names.
      // Each row in the table is its own form, but there is issue when switching between resource types.
      // To address the bug, we display each value with a unique form item name,
      // then strip the unique identifier before updating the dataSource,
      // which will be used to submit to the DB
      const rekeyedValues = {};
      Object.keys(values).forEach(key => {
        if (key.includes('-')) {
          rekeyedValues[key.slice(0, Math.max(0, key.indexOf('-')))] = values[key];
        } else {
          rekeyedValues[key] = values[key];
        }
      });

      handleSave({ ...record, ...rekeyedValues });
    } catch (errInfo) {
      // eslint-disable-next-line no-console
      console.log('Validation failed:', errInfo);
    }
  };

  let childNode = children;

  /** *
   * For most cells, return a string value which may be edited in an <Input />
   *
   * For the required and nullable columns, return a dropdown with true/false options if the user is in edit mode. Otherwise, return a string value.
   * @returns {JSX.Element|*}
   */
  const getCellContent = () => {
    if (enableEdits && (dataIndex === 'required' || dataIndex === 'nullable')) {
      const rules = [
        {
          required: true,
          message: 'Required',
        },
      ];
      let initialValue = 'true';

      if (record[dataIndex] === null) {
        initialValue = null;
      } else if (!record[dataIndex]) {
        initialValue = 'false';
      }

      return (
        <Form.Item
          style={{ margin: 0 }}
          name={record.id ? `${dataIndex}-${record.id}` : `${dataIndex}`}
          rules={rules}
          initialValue={initialValue}
          data-cy={`cell-${record.id}-${dataIndex}-select`}
        >
          <Select
            disabled={!editable}
            style={{ width: 120 }}
            onChange={() => {
              save();
            }}
            onBlur={() => {
              save();
            }}
          >
            <Option value="true" data-cy={`cell-${record.id}=${dataIndex}-select-true`}>
              true
            </Option>
            <Option value="false" data-cy={`cell-${record.id}=${dataIndex}-select-false`}>
              false
            </Option>
          </Select>
        </Form.Item>
      );
    }

    return (
      <div
        style={{
          overflowY: 'hidden',
          overflowWrap: 'break-word',
          lineHeight: '1.2em',
        }}
        data-cy={record ? `cell-${record.id}-${dataIndex}-display` : null}
      >
        {dataIndex === 'required' || dataIndex === 'nullable' ? childNode[1].toString() : childNode}
      </div>
    );
  };

  if (editable) {
    const rules = [];

    if (dataIndex === 'column_name') {
      rules.push({
        required: true,
        message: 'Required',
      });
      rules.push(() => ({
        validator(_, value) {
          if (value && dataSource.find(elem => elem.column_name === value)) {
            return Promise.reject(new Error('Column name must be unique'));
          }

          return Promise.resolve();
        },
      }));
    }

    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={record.id ? `${dataIndex}-${record.id}` : `${dataIndex}`}
        rules={rules}
        initialValue={record[dataIndex]}
        data-cy={`cell-${record.id}-${dataIndex}-input`}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
        data-cy={`cell-${record.id}-${dataIndex}`}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{getCellContent()}</td>;
};
